import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, UploadSimple, MagnifyingGlass } from "@phosphor-icons/react";
import CrudActions from "@/components/CrudActions";
import { toast } from "sonner";

const CITIES = {
  "London Plant": [51.5074, -0.1278], Reading: [51.4543, -0.9781], Oxford: [51.752, -1.2577],
  Cambridge: [52.2053, 0.1218], Brighton: [50.8225, -0.1372], "Milton Keynes": [52.0406, -0.7594],
  Croydon: [51.3762, -0.0982], Watford: [51.6565, -0.3903], Slough: [51.5105, -0.5954],
  Guildford: [51.2362, -0.5704], Birmingham: [52.4862, -1.8904],
};

export default function Shipments() {
  const { user } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const navigate = useNavigate();

  const isAdmin = user.role === "tenant_admin";

  const load = () => api.get("/shipments").then((r) => setShipments(r.data));
  useEffect(() => {
    load();
    if (isAdmin) {
      api.get("/vehicles").then((r) => setVehicles(r.data));
      api.get("/drivers").then((r) => setDrivers(r.data));
      api.get("/receivers").then((r) => setReceivers(r.data));
    }
  }, []); // eslint-disable-line

  const [receivers, setReceivers] = useState([]);
  const [form, setForm] = useState({
    batch_no: "", quantity_tons: 25, origin: "London Plant", destination: "Reading",
    vehicle_id: "", driver_id: "", receiver_id: "none", product: "OPC 52.5N Cement",
  });

  const create = async () => {
    if (!form.vehicle_id || !form.driver_id) return toast.error("Select a vehicle and driver");
    setBusy(true);
    try {
      const [olat, olng] = CITIES[form.origin];
      const [dlat, dlng] = CITIES[form.destination];
      await api.post("/shipments", {
        batch_no: form.batch_no || `BATCH-${Date.now().toString().slice(-4)}`,
        source_plant: form.origin, product: form.product, quantity_tons: Number(form.quantity_tons),
        origin: { name: form.origin, lat: olat, lng: olng },
        destination: { name: form.destination, lat: dlat, lng: dlng },
        vehicle_id: form.vehicle_id, driver_id: form.driver_id, planned_checkpoints: 3,
        receiver_id: form.receiver_id && form.receiver_id !== "none" ? form.receiver_id : null,
      });
      toast.success("Shipment created with Digital ID + QR");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/shipments/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} shipments${data.errors.length ? `, ${data.errors.length} errors` : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    e.target.value = "";
  };

  const [editS, setEditS] = useState(null);
  const openEdit = (s) => setEditS({
    id: s.id, batch_no: s.batch_no, product: s.product, quantity_tons: s.quantity_tons,
    status: s.status, originName: s.origin.name, destName: s.destination.name,
    vehicle_id: s.vehicle_id, driver_id: s.driver_id, receiver_id: s.receiver_id || "none",
  });
  const saveShip = async () => {
    try {
      const payload = {
        batch_no: editS.batch_no, product: editS.product, quantity_tons: Number(editS.quantity_tons),
        receiver_id: editS.receiver_id && editS.receiver_id !== "none" ? editS.receiver_id : null,
      };
      if (editS.status === "created") {
        const [olat, olng] = CITIES[editS.originName];
        const [dlat, dlng] = CITIES[editS.destName];
        payload.origin = { name: editS.originName, lat: olat, lng: olng };
        payload.destination = { name: editS.destName, lat: dlat, lng: dlng };
        payload.vehicle_id = editS.vehicle_id;
        payload.driver_id = editS.driver_id;
      }
      await api.put(`/shipments/${editS.id}`, payload);
      toast.success("Shipment updated"); setEditS(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delShip = async (id) => {
    try { await api.delete(`/shipments/${id}`); toast.success("Shipment deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const filtered = shipments.filter((s) => {
    const mq = `${s.code} ${s.batch_no} ${s.origin.name} ${s.destination.name} ${s.driver_name}`.toLowerCase().includes(q.toLowerCase());
    const ms = statusFilter === "all" || s.status === statusFilter;
    return mq && ms;
  });

  return (
    <Layout title="Shipments" subtitle="Digital IDs, QR codes & lifecycle"
      actions={isAdmin && (
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".csv" hidden onChange={onImport} data-testid="csv-input" />
          <Button variant="outline" data-testid="import-csv-btn" onClick={() => fileRef.current?.click()}>
            <UploadSimple size={16} className="mr-1.5" /> Import CSV
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-shipment-btn" className="bg-slate-900 hover:bg-slate-800">
                <Plus size={16} className="mr-1.5" /> New Shipment
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">Create Shipment</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-2">
                <Field label="Batch No"><Input data-testid="f-batch" value={form.batch_no} onChange={(e) => setForm({ ...form, batch_no: e.target.value })} placeholder="BATCH-2408" /></Field>
                <Field label="Quantity (tons)"><Input data-testid="f-qty" type="number" value={form.quantity_tons} onChange={(e) => setForm({ ...form, quantity_tons: e.target.value })} /></Field>
                <Field label="Origin">
                  <SelectBox testid="f-origin" value={form.origin} onChange={(v) => setForm({ ...form, origin: v })} options={Object.keys(CITIES)} />
                </Field>
                <Field label="Destination">
                  <SelectBox testid="f-dest" value={form.destination} onChange={(v) => setForm({ ...form, destination: v })} options={Object.keys(CITIES)} />
                </Field>
                <Field label="Vehicle">
                  <SelectBox testid="f-vehicle" value={form.vehicle_id} onChange={(v) => setForm({ ...form, vehicle_id: v })}
                    options={vehicles.map((v) => ({ value: v.id, label: `${v.reg_number} · ${v.vehicle_type_label}` }))} placeholder="Select vehicle" />
                </Field>
                <Field label="Driver">
                  <SelectBox testid="f-driver" value={form.driver_id} onChange={(v) => setForm({ ...form, driver_id: v })}
                    options={drivers.map((d) => ({ value: d.id, label: d.name }))} placeholder="Select driver" />
                </Field>
                <div className="col-span-2">
                  <Field label="Receiver (optional)">
                    <SelectBox testid="f-receiver" value={form.receiver_id} onChange={(v) => setForm({ ...form, receiver_id: v })}
                      options={[{ value: "none", label: "Unassigned — any receiver can confirm" }, ...receivers.map((r) => ({ value: r.id, label: `${r.name} · ${r.email}` }))]} />
                  </Field>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="submit-shipment" onClick={create} disabled={busy} className="bg-slate-900 hover:bg-slate-800">
                  {busy ? "Creating…" : "Create + Generate QR"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    >
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="shipment-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code, route, driver…" className="pl-9 bg-white" />
        </div>
        <SelectBox testid="status-filter" value={statusFilter} onChange={setStatusFilter}
          options={[{ value: "all", label: "All statuses" }, { value: "created", label: "Created" }, { value: "dispatched", label: "Dispatched" }, { value: "in_transit", label: "In Transit" }, { value: "delivered", label: "Delivered" }]}
          className="sm:w-48" />
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-5 py-2.5 font-bold">Code</th>
                <th className="px-5 py-2.5 font-bold">Batch</th>
                <th className="px-5 py-2.5 font-bold">Route</th>
                <th className="px-5 py-2.5 font-bold">Vehicle</th>
                <th className="px-5 py-2.5 font-bold">Distance</th>
                <th className="px-5 py-2.5 font-bold">CO₂</th>
                <th className="px-5 py-2.5 font-bold">Status</th>
                {isAdmin && <th className="px-5 py-2.5 font-bold text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} data-testid={`shipment-row-${s.code}`} onClick={() => navigate(`/shipments/${s.id}`)}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <td className="px-5 py-3 font-mono font-medium text-slate-900">{s.code}</td>
                  <td className="px-5 py-3 text-slate-600">{s.batch_no}</td>
                  <td className="px-5 py-3 text-slate-600">{s.origin.name} → {s.destination.name}</td>
                  <td className="px-5 py-3 text-slate-600">{s.vehicle?.reg_number || "—"}</td>
                  <td className="px-5 py-3 font-mono text-slate-600">{s.distance_km} km</td>
                  <td className="px-5 py-3 font-mono text-slate-600">{s.co2_kg} kg</td>
                  <td className="px-5 py-3"><StatusBadge status={s.status} /></td>
                  {isAdmin && (
                    <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end">
                        <CrudActions testidBase={`shipment-${s.code}`} name="shipment" canDelete
                          onEdit={() => openEdit(s)} onDelete={() => delShip(s.id)} />
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={isAdmin ? 8 : 7} className="px-5 py-10 text-center text-slate-400">No shipments found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {editS && (
        <Dialog open={!!editS} onOpenChange={(o) => !o && setEditS(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">Edit Shipment</DialogTitle></DialogHeader>
            {editS.status !== "created" && (
              <p className="text-xs text-slate-500 -mt-1">Route, vehicle & driver are locked once a shipment is dispatched.</p>
            )}
            <div className="grid grid-cols-2 gap-4 py-2">
              <Field label="Batch No"><Input data-testid="edit-s-batch" value={editS.batch_no} onChange={(e) => setEditS({ ...editS, batch_no: e.target.value })} /></Field>
              <Field label="Quantity (tons)"><Input data-testid="edit-s-qty" type="number" value={editS.quantity_tons} onChange={(e) => setEditS({ ...editS, quantity_tons: e.target.value })} /></Field>
              <div className="col-span-2"><Field label="Product"><Input data-testid="edit-s-product" value={editS.product} onChange={(e) => setEditS({ ...editS, product: e.target.value })} /></Field></div>
              <div className="col-span-2"><Field label="Receiver">
                <SelectBox testid="edit-s-receiver" value={editS.receiver_id} onChange={(v) => setEditS({ ...editS, receiver_id: v })}
                  options={[{ value: "none", label: "Unassigned — any receiver can confirm" }, ...receivers.map((r) => ({ value: r.id, label: `${r.name} · ${r.email}` }))]} />
              </Field></div>
              {editS.status === "created" && (
                <>
                  <Field label="Origin"><SelectBox testid="edit-s-origin" value={editS.originName} onChange={(v) => setEditS({ ...editS, originName: v })} options={Object.keys(CITIES)} /></Field>
                  <Field label="Destination"><SelectBox testid="edit-s-dest" value={editS.destName} onChange={(v) => setEditS({ ...editS, destName: v })} options={Object.keys(CITIES)} /></Field>
                  <Field label="Vehicle"><SelectBox testid="edit-s-vehicle" value={editS.vehicle_id} onChange={(v) => setEditS({ ...editS, vehicle_id: v })} options={vehicles.map((v) => ({ value: v.id, label: `${v.reg_number} · ${v.vehicle_type_label}` }))} /></Field>
                  <Field label="Driver"><SelectBox testid="edit-s-driver" value={editS.driver_id} onChange={(v) => setEditS({ ...editS, driver_id: v })} options={drivers.map((d) => ({ value: d.id, label: d.name }))} /></Field>
                </>
              )}
            </div>
            <DialogFooter><Button data-testid="save-shipment" onClick={saveShip} className="bg-slate-900 hover:bg-slate-800">Save Changes</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function SelectBox({ value, onChange, options, placeholder, testid, className }) {
  const opts = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className={`bg-white ${className || ""}`}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {opts.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testid}-${o.value}`}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
